import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { usePageMeta } from '../lib/usePageMeta';

const tools = [
  {
    title: 'Character Count Online',
    description: 'Count characters, words, sentences, and paragraphs instantly. Perfect for writers, SEO teams, and editors working with strict character limits.',
    url: '/tools/character-count-online/',
  },
  {
    title: 'Word Count Online',
    description: 'Quickly count words, characters, sentences, and paragraphs in your text. Ideal for writers, students, and SEO professionals.',
    url: '/tools/word-count-online/',
  },
  {
    title: 'Case Converter Online',
    description: 'Convert text to uppercase, lowercase, sentence case, title case, and more. Perfect for formatting headings, titles, and documents.',
    url: '/tools/case-converter-online/',
  },
  {
    title: 'Remove Extra Spaces Online',
    description: 'Clean up unnecessary spaces from your text instantly. Remove duplicate spaces between words for clean, consistent formatting.',
    url: '/tools/remove-extra-spaces-online/',
  },
  {
    title: 'AI SEO Content Brief Generator',
    description: 'Generate SEO-ready content briefs and outlines in seconds. Define keywords, search intent, and audience to create structured blog outlines for your writers.',
    url: '/tools/ai-seo-content-brief-generator/',
  },
];

export default function ToolsHub() {
  const navigate = useNavigate();
  usePageMeta({
    title: 'Content Tools For You - Wricoo',
    description: 'Free content tools for writers, SEO teams, and editors. Character counter, word counter, case converter, and more.',
    robots: 'index, follow',
    canonical: 'https://wricoo.com/tools/',
  });

  return (
    <div className="min-h-screen bg-white" data-testid="tools-hub-page">

      {/* Header */}
      <header className="border-b border-blue-200 bg-blue-600 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-blue-200 hover:text-white transition"><ArrowLeft className="w-5 h-5" /></Link>
            <Link to="/" className="text-xl font-bold text-white tracking-tight">Wricoo</Link>
          </div>
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/about/" className="text-sm font-medium text-blue-100 hover:text-white transition">About</Link>
            <Link to="/pricing/" className="text-sm font-medium text-blue-100 hover:text-white transition">Pricing</Link>
            <Link to="/tools/" className="text-sm font-medium text-white transition">Tools</Link>
          </nav>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="text-white hover:bg-blue-500 hover:text-white" onClick={() => navigate('/login/')}>Sign In</Button>
            <Button size="sm" className="bg-white text-blue-600 hover:bg-blue-50" onClick={() => navigate('/register/')}>Get Started</Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-b from-blue-50 to-white py-16 sm:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h1 className="font-bold text-gray-900 leading-tight mb-4">Content Tools For You</h1>
          <p className="text-gray-600 leading-relaxed">Free tools to help writers, SEO teams, and editors work faster and smarter.</p>
        </div>
      </section>

      {/* Tools Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="grid sm:grid-cols-2 gap-6">
            {tools.map((tool, i) => (
              <div key={i} className="border border-gray-200 rounded p-6 flex flex-col" data-testid={`tool-card-${i}`}>
                <h2 className="font-bold text-blue-600 mb-3 text-left">{tool.title}</h2>
                <p className="text-gray-600 leading-relaxed mb-5 text-left flex-1">{tool.description}</p>
                <div className="text-left">
                  <Button size="sm" onClick={() => navigate(tool.url)} data-testid={`tool-cta-${i}`}>
                    USE THIS <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-gray-500">&copy; {new Date().getFullYear()} Wricoo. All rights reserved.</p>
          <nav className="flex gap-4 text-sm text-gray-500">
            <a href="/about/" className="hover:text-gray-900 transition">About</a>
            <a href="/pricing/" className="hover:text-gray-900 transition">Pricing</a>
            <a href="/tools/" className="hover:text-gray-900 transition">Tools</a>
            <a href="/privacy-policy/" className="hover:text-gray-900 transition">Privacy Policy</a>
            <a href="/terms-and-conditions/" className="hover:text-gray-900 transition">Terms</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
